/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useParams } from 'js/lib/useRouter';

import { Button, ContextualHelp, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';

import SkillFocusTag from 'bundles/page-config-common/components/enterprise/SkillFocusTag';
import SkillsFocusModule from 'bundles/page-config-common/components/enterprise/SkillsFocusModule';
import HomeSection from 'bundles/page-config-common/components/shared/HomeSection';
import { useHeaderLevel } from 'bundles/page-config-common/contexts/HeaderLevelContext';
import { useEnterpriseSkills } from 'bundles/page-config-common/contexts/enterprise/EnterpriseSkillsContext';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { MY_LEARNING_QUERY_PARAM, QUERY_PARAM_VALUES } from 'bundles/unified-home-common/constants/MyLearningConstants';

import _t from 'i18n!nls/program-home';

const styles = {
  viewAllLink: css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-150);
  `,
  // align contextual help label with cta
  defaultTitle: css`
    ${breakpoints.up('sm')} {
      padding-top: var(--cds-spacing-100);
    }
  `,
};

const SkillFocusModuleRenderer = () => {
  const { adminSelectedSkills, areAdminSelectedSkillsLoading, hasProgramSpecificSkills } = useEnterpriseSkills();
  const { programSlug } = useParams();
  const features = useEnterpriseProductFeatures();
  const thirdPartyOrg = useThirdPartyOrganization();
  const { currentLevel } = useHeaderLevel();
  const isMobile = useMediaQuery(breakpoints.down('xs'));

  if (!areAdminSelectedSkillsLoading && !adminSelectedSkills?.length) {
    return null;
  }

  const showViewAllButton = !hasProgramSpecificSkills && features?.enableSkillsDashboard;
  const defaultHeader = (
    <ContextualHelp
      css={showViewAllButton ? styles.defaultTitle : {}}
      helpToggleProps={{
        'aria-label': _t('Information about recommended skills'),
      }}
      tooltipProps={{
        closeButtonProps: {
          'aria-label': _t('Close information about recommended skills'),
        },
      }}
      label={
        <Typography2 variant="subtitleLarge" component={currentLevel}>
          {_t('Skills for you')}
        </Typography2>
      }
    >
      <Typography2 variant="bodyPrimary" component="p">
        {_t('Build deep proficiency with expertly matched recommendations.')}
      </Typography2>
    </ContextualHelp>
  );

  return (
    <HomeSection className="PageConfig_SkillsFocusComponent">
      <SkillsFocusModule
        loading={!!areAdminSelectedSkillsLoading}
        header={
          hasProgramSpecificSkills ? (
            <Typography2 variant="subtitleLarge" component={currentLevel}>
              {_t('Skills selected by #{orgName}', { orgName: thirdPartyOrg?.name || _t('your organization') })}
            </Typography2>
          ) : (
            defaultHeader
          )
        }
        cta={
          showViewAllButton ? (
            <Button
              component="a"
              variant="ghost"
              href={`/programs/${programSlug}/my-learning?${MY_LEARNING_QUERY_PARAM}=${QUERY_PARAM_VALUES.SKILLS_DASHBOARD}`}
              data-testid="view-all-skills-link"
              size="small"
              fullWidth={isMobile}
              edgeAlign="end"
            >
              {_t('View all Skills')}
            </Button>
          ) : null
        }
        skillCards={adminSelectedSkills?.map((skill, order) => (
          // @ts-expect-error TS2322 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
          <SkillFocusTag key={skill.id} skill={skill} index={order} programSlug={programSlug} source="program-home" />
        ))}
      />
    </HomeSection>
  );
};

export default SkillFocusModuleRenderer;
